import { supabase } from "@/supabase";

// Cua de canvis pendents de desar a Supabase. Es guarda al navegador perquè no es perdi
// si es recarrega la pàgina o es talla la connexió, i s'envia en ordre, un a un.

const PENDING_KEY = "race_mrt_pending";
const RETRY_MS = 5000;

function readPending() {
    try {
        return JSON.parse(localStorage.getItem(PENDING_KEY)) || [];
    } catch (e) {
        return [];
    }
}

export function savePending(pending) {
    try {
        localStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    } catch (e) {
        console.warn("No s'han pogut guardar els canvis pendents", e);
    }
}

const run = {
    addLap: (op) =>
        supabase.rpc("add_lap", { p_id: op.id, p_lap: op.lap, p_time: op.time }),
    removeLap: (op) => supabase.rpc("remove_lap", { p_id: op.id, p_lap: op.lap }),
    resetLaps: (op) =>
        supabase.from("racers").update({ laps: [] }).eq("category", op.category).select(),
    addRacer: (op) => supabase.from("racers").insert(op.racer).select(),
    updateRacer: (op) =>
        supabase.from("racers").update(op.fields).eq("id", op.id).select(),
    deleteRacer: (op) => supabase.from("racers").delete().eq("id", op.id),
    clearRacers: () => supabase.from("racers").delete().neq("id", ""),
    updateCategory: (op) =>
        supabase.from("categories").update(op.fields).eq("id", op.id).select(),
};

// Errors de xarxa o del servidor: es tornarà a provar. La resta (p. ex. dorsal duplicat) no.
function isRetryable(result) {
    return [0, 408, 429].includes(result.status) || result.status >= 500;
}

function describe(op) {
    if (op.type === "addRacer") return "nou participant (dorsal " + op.racer.number + ")";
    if (op.type === "updateRacer") return "canvis del participant";
    if (op.type === "deleteRacer") return "esborrar participant";
    if (op.type === "addLap" || op.type === "removeLap") return "volta";
    if (op.type === "updateCategory") return "estat de la cursa";
    return "canvi";
}

function describeError(error) {
    if (error.code === "23505") return "aquest dorsal ja existeix";
    return error.message;
}

let flushing = false;
let retryTimer = null;

export const namespaced = true;

export const state = {
    status: "loading", // loading | config | error | ready
    online: true,
    pending: readPending(),
    notice: null,
};

export const mutations = {
    setStatus(state, value) {
        state.status = value;
    },
    setOnline(state, value) {
        state.online = value;
    },
    push(state, op) {
        state.pending = [...state.pending, op];
    },
    shift(state) {
        state.pending = state.pending.slice(1);
    },
    notify(state, message) {
        state.notice = message;
    },
};

export const actions = {
    enqueue(context, op) {
        context.commit("push", op);
        context.dispatch("flush");
    },

    async flush(context) {
        if (flushing || !supabase) return;
        flushing = true;
        clearTimeout(retryTimer);

        try {
            while (context.state.pending.length) {
                const op = context.state.pending[0];
                let result;
                try {
                    result = await run[op.type](op);
                } catch (error) {
                    result = { error, status: 0 };
                }

                if (result.error && isRetryable(result)) {
                    context.commit("setOnline", false);
                    retryTimer = setTimeout(() => context.dispatch("flush"), RETRY_MS);
                    return;
                }

                context.commit("setOnline", true);

                if (result.error) {
                    console.error("Canvi rebutjat per Supabase", op, result.error);
                    context.commit(
                        "notify",
                        "No s'ha pogut desar: " + describe(op) + " (" + describeError(result.error) + ")"
                    );
                } else if (op.type === "updateCategory") {
                    context.commit("categories/applyRows", result.data, { root: true });
                } else if (op.type === "deleteRacer") {
                    context.commit("racers/removeRacer", op.id, { root: true });
                } else if (op.type === "clearRacers") {
                    context.commit("racers/setRacers", {}, { root: true });
                } else {
                    context.commit("racers/applyRows", result.data, { root: true });
                }

                context.commit("shift");
            }
        } finally {
            flushing = false;
        }
    },
};
